import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Mic2, Search, Users } from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { PageHero } from "@/components/PageHero";
import { PAGE_CONTAINER } from "@/components/PageContainer";
import { Input } from "@/components/ui/input";
import { getCommunityArtists, searchArtists } from "@/lib/artists.functions";
import { thumbUrl } from "@/lib/image-thumb";

export const Route = createFileRoute("/artistas/")({
  pendingMs: 0,
  pendingMinMs: 300,
  head: () => ({
    meta: [
      { title: "Artistas em vinil — Viny Club" },
      {
        name: "description",
        content:
          "Descubra artistas, veja a discografia disponível em vinil e saiba quais discos aparecem em mais coleções da comunidade Viny.",
      },
      { property: "og:title", content: "Artistas em vinil — Viny Club" },
      {
        property: "og:description",
        content: "Discografias em vinil e os artistas mais presentes nas coleções da Viny.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: ArtistsIndexPage,
});

function ArtistsIndexPage() {
  const [term, setTerm] = useState("");
  const [submitted, setSubmitted] = useState("");
  const searchFn = useServerFn(searchArtists);
  const rankingFn = useServerFn(getCommunityArtists);

  useEffect(() => {
    const id = setTimeout(() => setSubmitted(term.trim()), 400);
    return () => clearTimeout(id);
  }, [term]);

  const results = useQuery({
    queryKey: ["artists-search", submitted],
    queryFn: () => searchFn({ data: { q: submitted } }),
    enabled: submitted.length > 1,
    staleTime: 5 * 60_000,
  });

  const ranking = useQuery({
    queryKey: ["community-artists", 48],
    queryFn: () => rankingFn({ data: { limit: 48 } }),
    staleTime: 10 * 60_000,
  });

  const showSearch = submitted.length > 1;
  const list = showSearch ? results.data ?? [] : ranking.data ?? [];
  const loading = showSearch ? results.isLoading : ranking.isLoading;

  return (
    <AppShell>
      <PageHero
        eyebrow="Descobrir"
        title="Artistas"
        subtitle="Discografias em vinil e os artistas mais presentes nas coleções da comunidade Viny."
      >
        <div className="relative max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
          <Input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Buscar artista"
            aria-label="Buscar artista"
            className="h-12 rounded-full border-white/10 bg-white/[0.06] pl-10 text-base text-white placeholder:text-white/40"
          />
        </div>
      </PageHero>

      <div className={PAGE_CONTAINER}>
        <div className="mb-4">
          <h2 className="font-display text-lg font-semibold">
            {showSearch ? `Resultados para "${submitted}"` : "Artistas mais presentes na comunidade"}
          </h2>
          {!showSearch && (
            <p className="mt-1 text-sm text-muted-foreground">
              Os artistas que mais aparecem nas coleções da Viny.
            </p>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-3 gap-4 sm:grid-cols-4 lg:grid-cols-6">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="animate-pulse text-center">
                <div className="mx-auto aspect-square w-full rounded-full bg-white/[0.06]" />
                <div className="mx-auto mt-3 h-3 w-16 rounded-full bg-white/[0.05]" />
              </div>
            ))}
          </div>
        ) : list.length === 0 ? (
          <p className="pb-10 text-sm text-muted-foreground">
            {showSearch ? "Nenhum artista encontrado." : "Ainda não há dados suficientes."}
          </p>
        ) : (
          <ul className="grid grid-cols-3 gap-4 pb-10 sm:grid-cols-4 lg:grid-cols-6">
            {list.map((a) => (
              <li key={a.slug}>
                <Link
                  to="/artistas/$slug"
                  params={{ slug: a.slug }}
                  search={{ n: a.name }}
                  className="group block text-center"
                >
                  <span className="mx-auto grid aspect-square w-full place-items-center overflow-hidden rounded-full border-2 border-[color:var(--hairline)] bg-surface-elevated transition group-hover:border-[color:var(--action)]">
                    {a.imageUrl ? (
                      <img
                        src={thumbUrl(a.imageUrl, 320) ?? undefined}
                        alt={a.name}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <Mic2 className="h-6 w-6 text-muted-foreground" />
                    )}

                  </span>
                  <span className="mt-2 block truncate text-sm font-semibold group-hover:text-[color:var(--action)]">
                    {a.name}
                  </span>
                  {a.collectorsCount > 0 && (
                    <span className="mt-0.5 flex items-center justify-center gap-1 text-[11px] text-muted-foreground">
                      <Users className="h-3 w-3" />
                      {a.collectorsCount} colecionador{a.collectorsCount === 1 ? "" : "es"}
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AppShell>
  );
}
